import React, { useState } from 'react';
import { Key, User, Mail, Phone, AlertCircle } from 'lucide-react';
import Modal from './Modal';
import { toast } from 'react-toastify';
import api from './api';

const CreateEtudiantAccountModal = ({ 
  isOpen, 
  onClose, 
  etudiant, 
  onSuccess 
}) => {
  const [selectedUsernameType, setSelectedUsernameType] = useState('matricule');
  const [availableUsernameTypes, setAvailableUsernameTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accountCreationData, setAccountCreationData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // Vérifier les informations disponibles quand l'étudiant change
  React.useEffect(() => {
    if (etudiant && isOpen) {
      const types = [];
      if (etudiant.matricule) types.push('matricule');
      if (etudiant.email) types.push('email');
      if (etudiant.contact) types.push('telephone');
      
      setAvailableUsernameTypes(types);
      
      // Définir le type par défaut
      if (types.includes('matricule')) {
        setSelectedUsernameType('matricule');
      } else if (types.includes('email')) {
        setSelectedUsernameType('email');
      } else if (types.includes('telephone')) {
        setSelectedUsernameType('telephone');
      }
    }
  }, [etudiant, isOpen]);

  const handleCreateAccount = async () => {
    if (!etudiant) return;
    
    setLoading(true);
    try {
      const response = await api.post('/utilisateurs/etudiant/create/', {
        etudiant_id: etudiant.id,
        username_type: selectedUsernameType
      });
      
      setAccountCreationData(response.data);
      setShowPassword(true);
      onSuccess && onSuccess(response.data);
      toast.success('Compte étudiant créé avec succès !');
    } catch (error) {
      console.error('Erreur lors de la création du compte:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowPassword(false);
    setAccountCreationData(null);
    setLoading(false);
    onClose();
  };

  const getUsernameValue = () => {
    switch (selectedUsernameType) {
      case 'matricule':
        return etudiant?.matricule || 'Non défini';
      case 'email':
        return etudiant?.email || 'Non défini';
      case 'telephone':
        return etudiant?.contact || 'Non défini';
      default:
        return 'Non défini';
    }
  };

  const getUsernameIcon = () => {
    switch (selectedUsernameType) {
      case 'matricule':
        return <User className="h-4 w-4" />;
      case 'email':
        return <Mail className="h-4 w-4" />;
      case 'telephone':
        return <Phone className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  if (showPassword && accountCreationData) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Compte étudiant créé avec succès !">
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <h4 className="text-sm font-medium text-green-800 mb-2">Informations du compte</h4>
            <div className="text-sm text-green-700 space-y-1">
              <p><strong>Nom d'utilisateur:</strong> {accountCreationData.user.username}</p>
              <p><strong>Nom complet:</strong> {accountCreationData.user.first_name} {accountCreationData.user.last_name}</p>
              <p><strong>Type:</strong> {accountCreationData.user.type_utilisateur}</p>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <h4 className="text-sm font-medium text-yellow-800 mb-2">⚠️ Mot de passe généré</h4>
            <div className="text-sm text-yellow-700">
              <p className="mb-2">Le mot de passe suivant a été généré automatiquement :</p>
              <div className="bg-white border border-yellow-300 rounded px-3 py-2 font-mono text-lg text-center">
                {accountCreationData.password}
              </div>
              <p className="mt-2 text-xs">
                <strong>Important:</strong> Notez ce mot de passe et transmettez-le à l'étudiant de manière sécurisée.
                Il ne sera plus affiché après la fermeture de cette fenêtre.
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
            >
              Compris
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Créer un compte étudiant">
      {etudiant && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Informations de l'étudiant</h4>
            <div className="text-sm text-blue-700">
              <p><strong>Nom:</strong> {etudiant.nom} {etudiant.prenom}</p>
              <p><strong>Matricule:</strong> {etudiant.matricule || 'Non défini'}</p>
              <p><strong>Email:</strong> {etudiant.email || 'Non défini'}</p>
              <p><strong>Téléphone:</strong> {etudiant.contact || 'Non défini'}</p>
            </div>
          </div>

          {availableUsernameTypes.length === 0 ? (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Impossible de créer un compte</h3>
                  <p className="text-sm text-red-700">
                    Cet étudiant n'a ni matricule, ni email, ni téléphone. Veuillez d'abord compléter ses informations.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type de nom d'utilisateur
                </label>
                <select
                  value={selectedUsernameType}
                  onChange={(e) => setSelectedUsernameType(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm"
                >
                  {availableUsernameTypes.includes('matricule') && (
                    <option value="matricule">Matricule ({etudiant.matricule})</option>
                  )}
                  {availableUsernameTypes.includes('email') && (
                    <option value="email">Email ({etudiant.email})</option>
                  )}
                  {availableUsernameTypes.includes('telephone') && (
                    <option value="telephone">Téléphone ({etudiant.contact})</option>
                  )}
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  Le mot de passe sera généré automatiquement
                </p>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                <div className="flex items-center space-x-2">
                  {getUsernameIcon()}
                  <span className="text-sm text-gray-600">
                    <strong>Nom d'utilisateur:</strong> {getUsernameValue()}
                  </span>
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleCreateAccount}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <Key className="h-4 w-4" />
                  {loading ? 'Création...' : 'Créer le compte'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default CreateEtudiantAccountModal;
